'use client';

import { ROUTES } from '../../../../../constants';
import React from 'react';
import { Box, Divider, ListItemIcon, Menu, MenuItem, Skeleton, Stack } from '@mui/material';
import { IconButton } from '../../../buttons';
import { AccountCircle, Logout, ManageAccounts } from '@mui/icons-material';
import { BodyLarge, Column } from '../../../../styles';
import { ProfilePicture } from '../../../profile-picture/ProfilePicture';
import { User } from '../../../../types/User';
import { useNavigateTo } from '../../../../hooks/useNavigateTo';
import { logout } from '../../../../auth/logout';
import { useTranslations } from 'next-intl';

type ProfileMenuProps = {
    user?: User;
};

export function ProfileMenu({ user }: ProfileMenuProps) {
    const navigateTo = useNavigateTo();
    const t_account = useTranslations('account');
    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

    if (!user) {
        return (
            <IconButton
                icon={
                    <Skeleton
                        animation='wave'
                        variant='circular'
                        width={40}
                        height={40}
                        style={{ padding: 1 }}
                    />
                }
            />
        );
    }

    const { profilePicture } = user;

    function closeMenu() {
        setAnchorElUser(null);
    }

    function handleOpenUserMenu(event: React.MouseEvent<HTMLElement>) {
        setAnchorElUser(event.currentTarget);
    }

    function onClickMenuItem(callback: () => void) {
        closeMenu();
        callback();
    }

    const menuItems = [
        {
            title: t_account('my_profile'),
            icon: <AccountCircle fontSize='small' />,
            callback: () => navigateTo(ROUTES.Profile)
        },

        {
            title: t_account('user_management'),
            icon: <ManageAccounts fontSize='small' />,
            callback: () => navigateTo(ROUTES.UserManagement)
        },
        {
            title: t_account('logout'),
            icon: <Logout fontSize='small' />,
            callback: () => logout(ROUTES.Home)
        }
    ];

    return (
        <Stack spacing={1} direction='row' alignItems='center'>
            <Box position={'relative'}>
                <IconButton
                    onClick={handleOpenUserMenu}
                    icon={<ProfilePicture size={40} imageUrl={profilePicture?.url} />}
                />
            </Box>
            <Menu
                sx={{ mt: '45px' }}
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={closeMenu}
            >
                {menuItems.map(({ title, icon, callback }, i) => (
                    <Column key={title}>
                        <MenuItem key={title} onClick={() => onClickMenuItem(callback)}>
                            <ListItemIcon>{icon}</ListItemIcon>
                            <BodyLarge>{title}</BodyLarge>
                        </MenuItem>
                        {i === menuItems.length - 2 && <Divider variant='middle' />}
                    </Column>
                ))}
            </Menu>
        </Stack>
    );
}
